import { Box, Stack } from '@src/components/Core';
import { IndicatorDotVariants } from '@src/components/IndicatorDot';
import { ScrollableBox } from '@src/components/ScrollableBox';
import { Tab, TabList, Tabs, TabSize, type TabProps } from '@src/components/Tabs';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { EntityAdminRecord } from '../../types';
import { useCustomDrawer } from './CustomDrawerContext';
import { TabProvider } from './TabContext';
import { useTabValidation } from './useTabValidation';

export interface TabDefinition extends Omit<TabProps, 'label' | 'suffix'> {
  label: string;
  component: ReactNode;
  suffix?: ReactNode;
}

function getTabProps(tab: TabDefinition): TabProps & { label: string } {
  const { component: _component, ...props } = tab;
  return props as TabProps & { label: string };
}

export interface CustomDrawerTabsProps {
  tabs: TabDefinition[];
  size?: TabSize;
  dataTestId?: string;
  header?: ReactNode;
}

export function CustomDrawerTabs<TDrawerRecord extends EntityAdminRecord>({
  tabs,
  size = TabSize.Large,
  dataTestId,
  header,
}: CustomDrawerTabsProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const { errorState, touchState, drawerOptions, setVisibleTabs } = useCustomDrawer<TDrawerRecord>();

  // Extract tab labels for validation
  const tabsToValidate = useMemo(() => {
    return tabs.map(tab => tab.label);
  }, [tabs]);

  // Register visible tabs with CustomDrawer for validation filtering
  useEffect(() => {
    setVisibleTabs(tabsToValidate);
  }, [tabsToValidate, setVisibleTabs]);

  // Compute error map for error dots
  const tabErrorMap = useTabValidation(errorState, touchState, drawerOptions, tabsToValidate);

  // Wrap tab components with TabProvider and prepare tabs for rendering
  const tabsWithProviders = useMemo(() => {
    return tabs.map(tab => ({
      ...tab,
      component: <TabProvider tabName={tab.label}>{tab.component}</TabProvider>,
    }));
  }, [tabs]);

  // Get the currently selected tab component
  const selectedTabComponent = tabsWithProviders[tabIndex]?.component ?? null;

  return (
    <Stack>
      {header && <Box>{header}</Box>}
      <Box flex="none" data-testid={dataTestId}>
        <Tabs selectedIndex={Math.min(tabIndex, tabs.length - 1)} onSelect={setTabIndex} size={size}>
          <TabList>
            {tabs.map(tab => (
              <Tab
                key={tab.label}
                {...getTabProps(tab)}
                showDot={tabErrorMap.get(tab.label) ?? false}
                dotVariant={IndicatorDotVariants.Negative}
              />
            ))}
          </TabList>
        </Tabs>
      </Box>
      <ScrollableBox outerProps={{ overflow: 'auto', h: '100%' }}>{selectedTabComponent}</ScrollableBox>
    </Stack>
  );
}
